import {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {NetworkContext} from '../lib/context';
import ProviderLocationsTable from '../components/provider-location-table';
import CustomTabs from '../components/tabs/tabs';
import LoadingModal from '../components/loading-modal';
import ToastNotifications from '../components/toast-notifications';
import {safeConsoleError} from '../../../lib/sanitize';
import {ERROR_DOMAINS} from '../../../lib/error-handler';
import handleError from '../../../lib/error';
import './myFiles.scss';

const MyFiles = observer(() => {
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const networkOptimizationStore = useContext(NetworkContext);

    // Use store's loading state and data directly
    const loading = isInitialLoading || networkOptimizationStore.loadingProviderRosters;
    const rows = networkOptimizationStore.providerRosters;
    const error = networkOptimizationStore.getDomainError(ERROR_DOMAINS.ROSTERS);

    const handleCopyRoster = async (rosterId: number, revisionName: string, notes: string) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.copyRoster(
                rosterId,
                revisionName,
                notes,
                abortController,
            );
        } catch (error) {
            safeConsoleError('Failed to copy roster:', error);
            throw error;
        }
    };

    const handleRenameRoster = async (
        rosterId: number,
        revisionId: number,
        revisionName: string,
        notes: string | null,
    ) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.renameRoster(
                rosterId,
                revisionId,
                revisionName,
                notes,
                abortController,
            );
        } catch (error) {
            safeConsoleError('Failed to copy roster:', error);
            throw error;
        }
    };

    const handleDeleteRoster = async (rosterId: number, revisionId: number) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.deleteRoster(rosterId, revisionId, abortController);
        } catch (error) {
            safeConsoleError('Failed to copy roster:', error);
            throw error;
        }
    };

    useEffect(() => {
        const abortController = new AbortController();
        setIsInitialLoading(true);
        networkOptimizationStore.reset();

        const loadData = async () => {
            try {
                // Use store's cached roster data instead of direct API call
                if (
                    networkOptimizationStore.providerRosters.length === 0 &&
                    !networkOptimizationStore.loadingProviderRosters
                ) {
                    await networkOptimizationStore.loadProviderRosters(abortController);
                }
                networkOptimizationStore.checkForExistingStreams();
            } catch (error) {
                // Set error in store so it can be displayed by ProviderLocationsTable
                handleError(error as Error, null, 'Failed to load My Files');
            } finally {
                setIsInitialLoading(false);
            }
        };

        loadData();
        return () => abortController.abort();
    }, [networkOptimizationStore]);

    return (
        <div className='min-h-[calc(100vh-11rem+2px)] w-full overflow-hidden myFiles'>
            <div className='flex items-center justify-between rounded px-12 py-8 mb-4 bg-white'>
                <div className='flex flex-col justify-center'>
                    <h3 className='text-3xl text-[#222B36] font-normal m-0'>My Files</h3>
                    <p className='text-base text-[#646A72] m-0'>
                        Choose to upload a new file or browse saved files
                    </p>
                </div>
            </div>
            <div className='grid grid-cols-6 gap-4 px-12 py-4'>
                <div className='col-span-6 rounded'>
                    <div className='w-full py-4 flex'>
                        <CustomTabs
                            variant='boxed'
                            noPadding={true}
                            tabs={[
                                {
                                    label: 'Provider/Facility Locations',
                                    content: (
                                        <ProviderLocationsTable
                                            rows={rows}
                                            loading={loading}
                                            error={error}
                                            onCopyRoster={handleCopyRoster}
                                            onRenameRoster={handleRenameRoster}
                                            onDeleteRoster={handleDeleteRoster}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Loading Modal */}
            <LoadingModal open={networkOptimizationStore.isRosterActionLoading} />

            <ToastNotifications />
        </div>
    );
});

export default MyFiles;
